import { SyncWarningBadge } from '@/components/shared/SyncStatusBadge';
import { VoiceoverAudioPlayer } from '@/components/shared/VoiceoverAudioPlayer';
import type { CrudConfig } from '@startupquickstart/frontend';
import { Languages } from 'lucide-react';

export interface AttractionTranslation {
  id: string;
  attractionId: string;
  languageId: string;
  voiceoverId: string | null;
  longVoiceoverId: string | null;
  name: string;
  description: string | null;
  longDescription: string | null;
  createdAt: string;
  updatedAt: string;
  attraction?: {
    id: string;
    name: string;
  };
  language?: {
    id: string;
    name: string;
    code: string;
  };
  voiceover?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    duration?: number;
    url?: string;
  };
  longVoiceover?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    duration?: number;
    url?: string;
  };
  // Sync status fields (computed by API)
  isNameOutdated?: boolean | null;
  isDescriptionOutdated?: boolean | null;
  isLongDescriptionOutdated?: boolean | null;
  isDescriptionVoiceoverInSync?: boolean | null;
  isLongDescriptionVoiceoverInSync?: boolean | null;
}

/**
 * Factory function to create attraction translations config for a specific attraction
 */
export const createAttractionTranslationsConfig = (
  attractionId: string,
  options?: {
    onGenerateTranslations?: () => void;
  },
): CrudConfig<AttractionTranslation> => ({
  entityName: 'Translation',
  entityNamePlural: 'Translations',

  endpoints: {
    list: `/attractions/${attractionId}/translations`,
    single: (id: string) => `/attractions/${attractionId}/translations/${id}`,
    // Translations are generated, not manually created (disabled via actions below)
    create: `/attractions/${attractionId}/translations`,
    update: (id: string) => `/attractions/${attractionId}/translations/${id}`,
    // Translations are generated, not manually deleted (disabled via actions below)
    delete: (id: string) => `/attractions/${attractionId}/translations/${id}`,
  },

  fields: [
    {
      key: 'language',
      label: 'Language',
      type: 'custom',
      required: false,
      format: (_value: AttractionTranslation['language'], fullRecord?: AttractionTranslation) => {
        if (!fullRecord?.language) return '—';
        return (
          <div>
            <div className="font-medium">{fullRecord.language.name}</div>
            <div className="text-xs text-muted-foreground">{fullRecord.language.code}</div>
          </div>
        );
      },
    },
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      format: (value: string, fullRecord?: AttractionTranslation) => {
        return (
          <div className="flex items-center gap-2">
            <span>{value}</span>
            <SyncWarningBadge
              isInSync={
                fullRecord?.isNameOutdated === true
                  ? false
                  : fullRecord?.isNameOutdated === false
                    ? true
                    : null
              }
              message="Source attraction name has changed since this translation was generated"
            />
          </div>
        );
      },
    },
    {
      key: 'description',
      label: 'Short Description',
      type: 'richtext',
      required: false,
      format: (value: string | null, fullRecord?: AttractionTranslation) => {
        return (
          <div className="flex flex-col gap-2">
            <div dangerouslySetInnerHTML={{ __html: value || '-' }} />
            <SyncWarningBadge
              isInSync={
                fullRecord?.isDescriptionOutdated === true
                  ? false
                  : fullRecord?.isDescriptionOutdated === false
                    ? true
                    : null
              }
              message="Source attraction description has changed since this translation was generated"
            />
          </div>
        );
      },
    },
    {
      key: 'longDescription',
      label: 'Long Description',
      type: 'richtext',
      required: false,
      format: (value: string | null, fullRecord?: AttractionTranslation) => {
        return (
          <div className="flex flex-col gap-2">
            <div dangerouslySetInnerHTML={{ __html: value || '-' }} />
            <SyncWarningBadge
              isInSync={
                fullRecord?.isLongDescriptionOutdated === true
                  ? false
                  : fullRecord?.isLongDescriptionOutdated === false
                    ? true
                    : null
              }
              message="Source attraction long description has changed since this translation was generated"
            />
          </div>
        );
      },
    },
    {
      key: 'voiceoverId',
      label: 'Short Description Voiceover',
      type: 'audio',
      required: false,
      readonly: true,
    },
    {
      key: 'voiceover',
      label: 'Short Description Voiceover',
      type: 'custom',
      required: false,
      format: (value: AttractionTranslation['voiceover'], fullRecord?: AttractionTranslation) => {
        return (
          <VoiceoverAudioPlayer
            voiceover={value}
            isInSync={fullRecord?.isDescriptionVoiceoverInSync ?? null}
            outOfSyncMessage="Description has changed since voiceover was generated"
          />
        );
      },
    },
    {
      key: 'longVoiceoverId',
      label: 'Long Description Voiceover',
      type: 'audio',
      required: false,
      readonly: true,
    },
    {
      key: 'longVoiceover',
      label: 'Long Description Voiceover',
      type: 'custom',
      required: false,
      format: (
        value: AttractionTranslation['longVoiceover'],
        fullRecord?: AttractionTranslation,
      ) => {
        return (
          <VoiceoverAudioPlayer
            voiceover={value}
            isInSync={fullRecord?.isLongDescriptionVoiceoverInSync ?? null}
            outOfSyncMessage="Long description has changed since voiceover was generated"
          />
        );
      },
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['language', 'name', 'createdAt', 'updatedAt'],
    searchableFields: ['name', 'description'],
    sortableFields: ['createdAt'],
    defaultSort: {
      field: 'createdAt',
      direction: 'desc',
    },
    itemsPerPage: 10,
    emptyState: {
      icon: <Languages className="h-12 w-12" />,
      title: 'No translations yet',
      description: 'Generate translations to make this attraction available in multiple languages',
      action: options?.onGenerateTranslations
        ? {
            label: 'Generate Translations',
            icon: Languages,
            onClick: options.onGenerateTranslations,
          }
        : undefined,
    },
    actions: {
      create: {
        enabled: false, // Translations are generated, not manually created
      },
      view: {
        enabled: true,
      },
      edit: {
        enabled: true,
      },
      delete: {
        enabled: false, // Translations are generated, not manually deleted
      },
    },
  },

  form: {
    sections: [
      {
        title: 'Translation Content',
        description: 'Edit translation text',
        fields: ['name', 'description', 'longDescription'],
        columns: 1,
      },
    ],
  },

  detailView: {
    sections: [
      {
        title: 'Translation Information',
        description: 'AI-generated translation content',
        fields: ['language', 'name'],
        defaultExpanded: true,
      },
      {
        title: 'Descriptions',
        description: 'Short and long translated descriptions',
        fields: ['voiceover', 'description', 'longVoiceover', 'longDescription'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },
});
